/* Dataset presentation rules. Pure — no React, no fetch.
 *
 * These live in an entity rather than in a screen because both the browser and the detail page make
 * the same judgements, and two copies of "when is a dataset trustworthy" is how they diverge.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';
import type { Dataset, DatasetVersion } from '@/shared/api/generated/catalog';

/** `schema.table`, the name an analyst actually recognises. */
export function qualifiedName(dataset: Dataset): string {
  return `${dataset.schema_name}.${dataset.object_name}`;
}

/** `vanished` gets `warn`, never `danger`.
 *
 * A dataset absent from the source is not broken and not an error — a table dropped after a
 * migration is expected, and its history is precisely what an auditor asks about. Rendering it in the
 * error colour trains people to treat a normal lifecycle event as an incident.
 */
export function datasetTone(dataset: Dataset): BadgeTone {
  return dataset.state === 'active' ? 'ok' : 'warn';
}

/** Whether renames on this dataset's source can be tracked at all.
 *
 * `source_object_id` is the rename anchor. Where a source does not supply one — MySQL, for instance —
 * a rename is indistinguishable from a drop plus a create, so schema history stays with the old
 * identity. Surfaced on the screen rather than left for someone to discover after a rename.
 */
export function tracksRenames(dataset: Dataset): boolean {
  return dataset.source_object_id !== null;
}

export interface SchemaChange {
  readonly kind: 'added' | 'removed' | 'retyped' | 'nullability' | 'comment';
  readonly column: string;
  readonly before: string | null;
  readonly after: string | null;
}

/** What changed between two adjacent versions.
 *
 * Computed in the client from two immutable snapshots rather than stored, because a stored diff is a
 * third representation that can disagree with the two it summarises. The versions themselves stay
 * authoritative.
 *
 * `comment` is included as a change kind on purpose: a comment edit produces a new version (it
 * changes the schema checksum) but does not invalidate a PII verdict (it does not change the
 * classification checksum). A history that hid it would make an unexplained version number.
 */
export function diffVersions(
  newer: DatasetVersion,
  older: DatasetVersion,
): readonly SchemaChange[] {
  const before = new Map(older.columns.map((column) => [column.name, column]));
  const after = new Map(newer.columns.map((column) => [column.name, column]));
  const changes: SchemaChange[] = [];

  for (const column of newer.columns) {
    const previous = before.get(column.name);
    if (previous === undefined) {
      changes.push({
        kind: 'added',
        column: column.name,
        before: null,
        after: column.data_type,
      });
      continue;
    }
    if (previous.data_type !== column.data_type) {
      changes.push({
        kind: 'retyped',
        column: column.name,
        before: previous.data_type,
        after: column.data_type,
      });
    }
    if (previous.nullable !== column.nullable) {
      changes.push({
        kind: 'nullability',
        column: column.name,
        before: previous.nullable ? 'NULL' : 'NOT NULL',
        after: column.nullable ? 'NULL' : 'NOT NULL',
      });
    }
    if (previous.comment !== column.comment) {
      changes.push({
        kind: 'comment',
        column: column.name,
        // Normalised to null: the generated type makes an absent comment `undefined` and an
        // explicitly-null one `null`, and a diff that distinguished them would report a change
        // where the source recorded none.
        before: previous.comment ?? null,
        after: column.comment ?? null,
      });
    }
  }

  for (const column of older.columns) {
    if (!after.has(column.name)) {
      changes.push({
        kind: 'removed',
        column: column.name,
        before: column.data_type,
        after: null,
      });
    }
  }

  return changes;
}

/** Whether a classification made against `checksum` still describes this version.
 *
 * The comparison is against the *classification* checksum, not the schema one. That distinction is
 * the whole of D04: a comment edit changes the schema checksum and creates a new version, but it
 * cannot change what a classifier would conclude, so an approved verdict survives it. Comparing
 * against the schema checksum instead would mark every verdict stale on a documentation pass.
 *
 * Both values are the ones the server stored, not ones recomputed on read — so a client can make
 * this judgement itself rather than trust a staleness flag it has no way to check.
 */
export function verdictStillApplies(version: DatasetVersion, checksum: string): boolean {
  return version.classification_checksum === checksum;
}
